"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { X, Megaphone } from "lucide-react";
import { cn } from "@/lib/utils";

interface BannerMessage {
  text: string;
  tab: string;
  /** label for the CTA portion of the text */
  cta: string;
}

const MESSAGES: BannerMessage[] = [
  {
    text: "Your crew is losing ~$2,000/yr in missed deductions.",
    cta: "Help them fix that",
    tab: "referral-program",
  },
  {
    text: "Got a clinic or association meeting coming up?",
    cta: "Grab the slide deck",
    tab: "slides",
  },
  {
    text: "Know an official who tracks mileage on napkins?",
    cta: "Send them the link",
    tab: "share",
  },
  {
    text: "Refer 3 fellow officials and get 50% off next season.",
    cta: "See how it works",
    tab: "referral-program",
  },
  {
    text: "Your partner deserves better tax records too.",
    cta: "Share OfficialsPay",
    tab: "share",
  },
  {
    text: "Earn free access by telling your crew about OfficialsPay.",
    cta: "Learn about referral rewards",
    tab: "referral-program",
  },
];

const ROTATION_INTERVAL_MS = 8000;
const SESSION_KEY = "referral-banner-dismissed";

export function ReferralBanner() {
  const router = useRouter();
  const [dismissed, setDismissed] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Check session storage on mount
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "true") {
        setDismissed(true);
      }
    } catch {
      // sessionStorage not available
    }
  }, []);

  // Slow rotation
  useEffect(() => {
    if (dismissed) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % MESSAGES.length);
        setIsTransitioning(false);
      }, 500);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dismissed]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(SESSION_KEY, "true");
    } catch {
      // sessionStorage not available
    }
  }, []);

  const handleClick = useCallback(() => {
    const message = MESSAGES[currentIndex];
    router.push(`/deals?tab=${message.tab}`);
  }, [currentIndex, router]);

  if (dismissed) return null;

  const message = MESSAGES[currentIndex];

  return (
    <div className="relative flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 mb-4">
      <Megaphone className="size-4 text-primary shrink-0" />
      <div
        className={cn(
          "flex-1 text-sm transition-opacity duration-500 ease-in-out cursor-pointer",
          isTransitioning ? "opacity-0" : "opacity-100"
        )}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleClick();
        }}
      >
        <span className="text-foreground">{message.text}</span>{" "}
        <span className="font-medium text-primary hover:underline">
          {message.cta} &rarr;
        </span>
      </div>
      <button
        onClick={handleDismiss}
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Dismiss referral banner"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
